import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/i18n";

const LS_KEY = "welworth.lang";
const CHOSEN_KEY = "welworth.lang.chosen";

/**
 * First-visit language picker. Blocks the page until the user picks
 * English or Marathi. Choice is persisted so the modal only appears once.
 *
 * Also exposes a global window.__openLanguageModal for the Navbar toggle.
 */
export default function LanguageModal() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(i18n.language?.startsWith("mr") ? "mr" : "en");

  useEffect(() => {
    const already = localStorage.getItem(CHOSEN_KEY);
    if (!already) setOpen(true);
    // expose a hook so Navbar can force-open the picker later
    window.__openLanguageModal = () => setOpen(true);
    return () => { delete window.__openLanguageModal; };
  }, []);

  const confirm = () => {
    i18n.changeLanguage(selected);
    localStorage.setItem(LS_KEY, selected);
    localStorage.setItem(CHOSEN_KEY, "1");
    setOpen(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 px-5 py-8 backdrop-blur-sm" data-testid="language-modal">
      <div className="w-full max-w-md bg-white rounded-sm shadow-2xl border border-black/10">
        <div className="px-7 py-6 border-b border-black/10">
          <div className="text-[10px] tracking-overline uppercase text-gray-500 font-bold">Welworth</div>
          <h2 className="mt-1 font-display text-2xl sm:text-3xl text-black font-semibold" data-testid="lang-modal-title">{t("langModal.title")}</h2>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">{t("langModal.subtitle")}</p>
        </div>
        <div className="px-7 py-6 grid grid-cols-1 gap-3">
          {SUPPORTED_LANGUAGES.map((L) => (
            <button
              key={L.code}
              onClick={() => setSelected(L.code)}
              className={
                "w-full text-left px-5 py-4 rounded-sm border-2 transition flex items-center justify-between " +
                (selected === L.code
                  ? "border-black bg-black text-white"
                  : "border-black/15 hover:border-black bg-white text-black")
              }
              data-testid={`lang-option-${L.code}`}
            >
              <div>
                <div className="font-display text-xl">{L.native}</div>
                <div className={"text-xs mt-0.5 " + (selected === L.code ? "text-white/70" : "text-gray-500")}>{L.label}</div>
              </div>
              <div className={
                "w-5 h-5 rounded-full border-2 flex items-center justify-center " +
                (selected === L.code ? "border-white" : "border-black/30")
              }>
                {selected === L.code && <div className="w-2.5 h-2.5 rounded-full bg-white"/>}
              </div>
            </button>
          ))}
        </div>
        <div className="px-7 py-5 border-t border-black/10 flex justify-end">
          <button
            onClick={confirm}
            className="bg-black text-white font-semibold px-6 py-3 rounded-sm hover:bg-gray-800 transition"
            data-testid="lang-modal-confirm"
          >
            {t("langModal.continue")}
          </button>
        </div>
      </div>
    </div>
  );
}
